import { ReactElement, useCallback, useMemo } from 'react'
import { Vector3Tuple } from 'three'
import { useElementIdToAssembly } from '@editor/hooks'
import { useStiffeningStore } from '@editor/stores'
import { getStiffeningSegmentPlanAxis, isPlanStiffeningSegment } from '@editor/utils'
import { LineShapesMesh } from '@modugen/ui-toolkit'
import { OpacityOption } from '@modugen/ui-toolkit/dist/scene/types/opacity'
import { ThreeEvent } from '@react-three/fiber'
import { calculateStructuralAssemblyThickness } from 'src/components/manager/assemblies/utils'

interface Props {
  /** Walls whose segments are drawn (the storey's visible wall footprints). */
  walls: ShapeObject[]
  guidToHidden?: Record<string, boolean>
  /** Same per-storey z translation the wall footprints use, so segments stay on their wall. */
  storeyToElementTypeAndZTranslation?: Record<string, Partial<Record<ElementTypes, number>>>
  /** Same map the wall footprints use: segments of wall types mapped to `false` ignore clicks. */
  typeToSelectable?: Partial<Record<ElementTypes, boolean>>
  /** Wall handlers: a click on a segment acts on its wall, like a click on a wall opening. */
  onClickElement?: (guid: string, event: ThreeEvent<MouseEvent>) => void
  onContextMenuElement?: (guid: string, event: ThreeEvent<MouseEvent>) => void
}

/** `STIFFENING_SEGMENT_FOOTPRINT_COLOR_HEX` / `_BORDER_COLOR_HEX` of the PDF export */
const SEGMENT_FOOTPRINT_COLOR = '#b8e2b6'
const SEGMENT_FOOTPRINT_BORDER_COLOR = '#000000'

const SEGMENT_BODY_HEIGHT = 0.01
// On top of the wall footprints (drawn at -0.02), like the export which adds the segment
// footprints after the wall footprints.
const Z_OFFSET = -0.01
// Same fallback as the wall footprints, so a segment always covers its wall.
const FALLBACK_WALL_THICKNESS = 0.2

const OPACITY_OPTION_VALUES: Record<OpacityOption, number> = {
  notTransparent: 1,
  transparent: 0.4,
}

/**
 * Stiffening segments as the stiffening plan PDF draws them ("Stempel Aussteifung"): a green
 * footprint along the segment's interval on the wall's center axis, as wide as the wall's
 * structural thickness. Clicks and context menus are forwarded to the segment's wall.
 */
const StiffeningSegmentsFootprintMesh = ({
  walls,
  guidToHidden,
  storeyToElementTypeAndZTranslation,
  typeToSelectable,
  onClickElement,
  onContextMenuElement,
}: Props): ReactElement | null => {
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  const elementIdToAssembly = useElementIdToAssembly()

  const { elements, guidToAdditionalTranslation, segmentGuidToWallGuid } = useMemo(() => {
    const wallsByGuid = new Map(walls.map(wall => [wall.guid, wall]))
    const elements: {
      id: string
      start: Vector3Tuple
      end: Vector3Tuple
      width: number
      height: number
    }[] = []
    const translations: Record<string, Vector3Tuple> = {}
    const segmentToWall: Record<string, string> = {}

    stiffeningIntervals.forEach(segment => {
      if (!isPlanStiffeningSegment(segment)) return
      const wall = wallsByGuid.get(segment.element_guid)
      if (!wall || guidToHidden?.[wall.guid]) return
      const axis = getStiffeningSegmentPlanAxis(wall, segment)
      if (!axis) return

      const assembly = elementIdToAssembly[wall.guid]
      const guid = segment.guid as string
      elements.push({
        id: guid,
        start: [axis.start.x, axis.start.y, axis.start.z],
        end: [axis.end.x, axis.end.y, axis.end.z],
        width:
          (assembly && calculateStructuralAssemblyThickness(assembly)) || FALLBACK_WALL_THICKNESS,
        height: SEGMENT_BODY_HEIGHT,
      })
      segmentToWall[guid] = wall.guid

      const elementType: ElementTypes =
        wall.placement === 'External' ? 'outer_walls' : 'inner_walls'
      const zTranslation = storeyToElementTypeAndZTranslation?.[wall.storey]?.[elementType] ?? 0
      if (zTranslation) translations[guid] = [0, 0, zTranslation]
    })

    return {
      elements,
      guidToAdditionalTranslation: translations,
      segmentGuidToWallGuid: segmentToWall,
    }
  }, [
    walls,
    guidToHidden,
    stiffeningIntervals,
    elementIdToAssembly,
    storeyToElementTypeAndZTranslation,
  ])

  const {
    guidToColor,
    guidToOutlinesColor,
    guidToHidden: noneHidden,
    guidToOpacity,
    guidToRenderMode,
  } = useMemo(() => {
    const colors: Record<string, string> = {}
    const outlines: Record<string, string> = {}
    const hidden: Record<string, boolean> = {}
    const opacity: Record<string, OpacityOption> = {}
    const renderMode: Record<string, { bodyGeometry: boolean; outline: boolean }> = {}
    elements.forEach(({ id }) => {
      colors[id] = SEGMENT_FOOTPRINT_COLOR
      outlines[id] = SEGMENT_FOOTPRINT_BORDER_COLOR
      hidden[id] = false
      opacity[id] = 'notTransparent'
      renderMode[id] = { bodyGeometry: true, outline: true }
    })
    return {
      guidToColor: colors,
      guidToOutlinesColor: outlines,
      guidToHidden: hidden,
      guidToOpacity: opacity,
      guidToRenderMode: renderMode,
    }
  }, [elements])

  // Like the wall footprints: segments of walls whose type is not selectable ignore clicks.
  const resolveSelectableWallGuid = useCallback(
    (guid: string): string | undefined => {
      const wallGuid = segmentGuidToWallGuid[guid]
      const wall = wallGuid ? walls.find(({ guid }) => guid === wallGuid) : undefined
      if (!wall) return undefined
      const elementType: ElementTypes =
        wall.placement === 'External' ? 'outer_walls' : 'inner_walls'
      if (typeToSelectable?.[elementType] === false) return undefined
      return wallGuid
    },
    [segmentGuidToWallGuid, walls, typeToSelectable],
  )

  const handleClick = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>) => {
      const wallGuid = resolveSelectableWallGuid(guid)
      if (wallGuid) onClickElement?.(wallGuid, event)
    },
    [onClickElement, resolveSelectableWallGuid],
  )

  const handleContextMenu = useCallback(
    (guid: string, event: ThreeEvent<MouseEvent>) => {
      const wallGuid = resolveSelectableWallGuid(guid)
      if (wallGuid) onContextMenuElement?.(wallGuid, event)
    },
    [onContextMenuElement, resolveSelectableWallGuid],
  )

  if (elements.length === 0) return null

  return (
    <LineShapesMesh
      elements={elements}
      guidToColor={guidToColor}
      guidToOutlinesColor={guidToOutlinesColor}
      guidToHidden={noneHidden}
      guidToOpacity={guidToOpacity}
      guidToRenderMode={guidToRenderMode}
      guidToAdditionalTranslation={guidToAdditionalTranslation}
      opacityOptionValues={OPACITY_OPTION_VALUES}
      zOffset={Z_OFFSET}
      handleClick={handleClick}
      handleContextMenu={handleContextMenu}
    />
  )
}

export default StiffeningSegmentsFootprintMesh
